import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Lock, Play, LogOut, Leaf, CheckCircle2, ShieldCheck, Heart } from "lucide-react";
import { SESSIONS_META_V2 as SESSIONS_META } from "@/data/sessions";
import { logout, api } from "@/lib/api";
import { motion } from "framer-motion";
import RemindersCard from "@/components/RemindersCard";
import OnboardingModal from "@/components/OnboardingModal";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api.get("/admin/is-admin").then(r => setIsAdmin(!!r.data.is_admin)).catch(() => {});
  }, []);

  // "Welcome back" toast: fires only if Landing set the flag on the current sign-in.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("healthapp.welcome_back");
      if (!raw) return;
      sessionStorage.removeItem("healthapp.welcome_back");
      const { firstName, lastSession } = JSON.parse(raw);
      const name = (firstName || "").trim();
      const nameLabel = name ? `Hola de nuevo, ${name}` : "Hola de nuevo";
      const description = lastSession != null
        ? `Continuamos donde lo dejaste — Sesión ${String(lastSession).padStart(2, "0")}.`
        : "Continuamos donde lo dejaste.";
      toast(nameLabel + " 💚", {
        description,
        duration: 6000,
        icon: <Heart className="w-4 h-4 text-[var(--ha-primary)]" strokeWidth={2} />,
      });
    } catch {}
  }, []);

  const onLogout = async () => {
    await logout();
    await refresh();
    navigate("/", { replace: true });
  };

  return (
    <main className="min-h-screen bg-[var(--ha-bg)]" data-testid="dashboard-page">
      <OnboardingModal />
      <header className="max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-full bg-[var(--ha-primary)] grid place-items-center shadow-sm">
            <Leaf className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-extrabold tracking-tight text-[var(--ha-text)] text-lg">MINDHOME HEALTH APP</span>
        </div>
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            {user?.picture && <AvatarImage src={user.picture} alt={user.name} />}
            <AvatarFallback>{(user?.name || "U").slice(0,1).toUpperCase()}</AvatarFallback>
          </Avatar>
          <span className="hidden sm:block text-sm text-[var(--ha-text)]">{user?.name}</span>
          {isAdmin && (
            <Button
              data-testid="nav-admin-btn"
              variant="outline"
              onClick={() => navigate("/admin")}
              className="rounded-full border-[var(--ha-primary)] text-[var(--ha-primary)] h-9 px-4"
            >
              <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={1.75} /> Admin
            </Button>
          )}
          <Button
            data-testid="logout-btn"
            variant="outline"
            onClick={onLogout}
            className="rounded-full border-[var(--ha-border)] h-9 px-4"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> Salir
          </Button>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-4 pb-16">
        <p className="text-xs uppercase tracking-[0.2em] text-[var(--ha-muted)] font-semibold mb-3">Tu programa</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--ha-text)]">Hola {user?.name?.split(" ")[0]}, bienvenido/a a tu recorrido</h1>
        <p className="mt-3 text-[var(--ha-muted)] max-w-2xl">
          Realiza cada sesión a tu ritmo. Podrás pausarla y retomarla más tarde, y tus respuestas quedarán guardadas de forma privada.
          <button
            data-testid="replay-onboarding-btn"
            onClick={() => { try { localStorage.removeItem("healthapp.onboarding_seen"); } catch {}; window.location.reload(); }}
            className="ml-2 underline text-[var(--ha-primary)] hover:text-[var(--ha-primary-hover)]"
          >Ver tutorial</button>
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <button
            data-testid="nav-progreso-card"
            onClick={() => navigate("/progreso")}
            className="text-left rounded-2xl border border-[var(--ha-border)] bg-white p-5 hover:border-[var(--ha-primary)] transition-colors"
          >
            <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Comparativa Pre y Post</p>
            <p className="mt-1.5 font-bold text-[var(--ha-text)]">Ver mi progreso →</p>
            <p className="mt-1 text-sm text-[var(--ha-muted)]">Compara tus puntuaciones de la Sesión 1 con las de la Sesión 8.</p>
          </button>
          <button
            data-testid="nav-anexos-card"
            onClick={() => navigate("/anexos")}
            className="text-left rounded-2xl border border-[var(--ha-border)] bg-white p-5 hover:border-[var(--ha-primary)] transition-colors"
          >
            <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Anexos de bienestar</p>
            <p className="mt-1.5 font-bold text-[var(--ha-text)]">Frases + respiración guiada →</p>
            <p className="mt-1 text-sm text-[var(--ha-muted)]">Frases diarias cada 6h y 4 timers guiados de respiración.</p>
          </button>
        </div>

        <div className="mt-4">
          <RemindersCard />
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-5" data-testid="sessions-grid">
          {SESSIONS_META.map((s, i) => (
            <motion.div
              key={s.number}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.4 }}
            >
              <Card
                className={`rounded-3xl border-[var(--ha-border)] bg-white shadow-sm ${s.unlocked ? "" : "opacity-70"}`}
                data-testid={`session-card-${s.number}`}
              >
                <CardContent className="p-6 flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-[0.18em] text-[var(--ha-muted)] font-semibold">Sesión {String(s.number).padStart(2,"0")}</p>
                    <h3 className="mt-2 font-bold text-lg text-[var(--ha-text)] leading-snug">{s.title}</h3>
                    <p className="mt-2 text-sm text-[var(--ha-muted)]">45-60 min · Ejercicios prácticos</p>
                    <div className="mt-4">
                      {s.unlocked ? (
                        <Button
                          data-testid={`open-session-${s.number}`}
                          onClick={() => navigate(`/sesion/${s.number}`)}
                          className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white"
                        >
                          <Play className="w-4 h-4 mr-2" strokeWidth={1.75} /> Comenzar
                        </Button>
                      ) : (
                        <Button
                          data-testid={`locked-session-${s.number}`}
                          disabled
                          variant="outline"
                          className="rounded-full border-[var(--ha-border)] text-[var(--ha-muted)]"
                        >
                          <Lock className="w-4 h-4 mr-2" strokeWidth={1.75} /> Próximamente
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className={`h-14 w-14 rounded-2xl grid place-items-center border ${s.unlocked ? "bg-[var(--ha-bg)] border-[var(--ha-border)]" : "bg-[var(--ha-border)]/40 border-transparent"}`}>
                    {s.unlocked ? (
                      <CheckCircle2 className="w-6 h-6 text-[var(--ha-primary)]" strokeWidth={1.5} />
                    ) : (
                      <Lock className="w-5 h-5 text-[var(--ha-muted)]" strokeWidth={1.5} />
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>
    </main>
  );
}
